import {
  argbFromHex,
  type DynamicColor,
  type DynamicScheme,
  Hct,
  hexFromArgb,
  MaterialDynamicColors,
  SchemeContent,
  SchemeExpressive,
  SchemeNeutral,
  SchemeTonalSpot,
  SchemeVibrant,
} from "@material/material-color-utilities";
import { ROLE_NAMES, type RoleColors, type RoleName } from "./roles.js";
import type { SchemeVariant } from "./types.js";

/** Build the Material dynamic scheme for a source color + variant + mode. */
function makeScheme(source: Hct, variant: SchemeVariant, isDark: boolean): DynamicScheme {
  switch (variant) {
    case "vibrant": return new SchemeVibrant(source, isDark, 0);
    case "expressive": return new SchemeExpressive(source, isDark, 0);
    case "content": return new SchemeContent(source, isDark, 0);
    case "neutral": return new SchemeNeutral(source, isDark, 0);
    case "tonal_spot":
    default: return new SchemeTonalSpot(source, isDark, 0);
  }
}

const MDC = new MaterialDynamicColors();

/** Generate the full chrome role set (hex) for one mode. Deterministic. */
export function generateScheme(sourceHex: string, variant: SchemeVariant, isDark: boolean): RoleColors {
  const scheme = makeScheme(Hct.fromInt(argbFromHex(sourceHex)), variant, isDark);
  const out = {} as RoleColors;
  for (const role of ROLE_NAMES) {
    const dynamic = (MDC as unknown as Record<RoleName, () => DynamicColor>)[role]();
    out[role] = hexFromArgb(scheme.getArgb(dynamic));
  }
  return out;
}

export interface KeyColors {
  primary: string;
  secondary: string;
  tertiary: string;
  neutral: string;
  neutralVariant: string;
}

/** The 5 Material "key colors" derived from the source (for display in the UI). */
export function keyColors(sourceHex: string, variant: SchemeVariant): KeyColors {
  const scheme = makeScheme(Hct.fromInt(argbFromHex(sourceHex)), variant, false);
  return {
    primary: hexFromArgb(scheme.primaryPaletteKeyColor),
    secondary: hexFromArgb(scheme.secondaryPaletteKeyColor),
    tertiary: hexFromArgb(scheme.tertiaryPaletteKeyColor),
    neutral: hexFromArgb(scheme.neutralPaletteKeyColor),
    neutralVariant: hexFromArgb(scheme.neutralVariantPaletteKeyColor),
  };
}
